<template>
  <div>
    <h2
      class="font-[Poppins] font-black text-[clamp(24px,3.5vw,34px)] text-[#191c1e] mb-1.5 tracking-[-0.5px]"
    >
      Navbar
    </h2>
    <p
      class="font-[Plus_Jakarta_Sans] text-[15px] text-[#6c7a77] leading-[1.65] mb-7 max-w-[580px]"
    >
      Sticky toppmeny på alla sidor. Desktop och mobil renderas av separata
      komponenter
      <span
        class="font-mono text-[13px] bg-[#f2f4f6] px-1.5 py-0.5 rounded-[5px]"
        >DesktopNav.vue</span
      >
      /
      <span
        class="font-mono text-[13px] bg-[#f2f4f6] px-1.5 py-0.5 rounded-[5px]"
        >MobileNav.vue</span
      >
      inuti
      <span
        class="font-mono text-[13px] bg-[#f2f4f6] px-1.5 py-0.5 rounded-[5px]"
        >Navigation.vue</span
      >. Höjd är 64&nbsp;px (
      <span
        class="font-mono text-[13px] bg-[#f2f4f6] px-1.5 py-0.5 rounded-[5px]"
        >h-16</span
      >).
    </p>

    <!-- Desktop -->
    <PreviewFull label="Desktop — navbar">
      <div
        class="w-full bg-white rounded-xl border border-[#eceef0] overflow-hidden"
      >
        <div class="px-6 h-16 flex items-center justify-between">
          <!-- Logo placeholder -->
          <div class="flex items-center gap-2.5 shrink-0">
            <EmmalagatLogo :size="48" />
          </div>

          <!-- Nav links -->
          <div class="flex items-center gap-8">
            <span
              class="relative text-[15px] font-semibold text-[#191c1e] cursor-pointer"
            >
              Alla recept
              <!-- Active underline -->
              <span
                class="absolute bottom-[-2px] left-0 right-0 h-0.5 bg-teal-500 rounded-full"
              />
            </span>
            <span class="text-[15px] font-normal text-[#3c4947] cursor-pointer"
              >Kategorier</span
            >
            <span class="text-[15px] font-normal text-[#3c4947] cursor-pointer"
              >Tekniker &amp; verktyg</span
            >
          </div>

          <!-- Icon buttons -->
          <div class="flex items-center gap-2">
            <button
              class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
            >
              <Search :size="18" class="text-[#3c4947]" />
            </button>
            <button
              class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
            >
              <Heart :size="18" class="text-[#3c4947]" />
            </button>
            <button
              class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
            >
              <User :size="18" class="text-[#3c4947]" />
            </button>
          </div>
        </div>
      </div>
    </PreviewFull>

    <!-- Desktop search open -->
    <PreviewFull label="Desktop — sök expanderad">
      <div
        class="w-full bg-white rounded-xl border border-[#eceef0] overflow-hidden"
      >
        <div class="px-6 h-16 flex items-center justify-between">
          <div class="flex items-center gap-2.5 shrink-0">
            <EmmalagatLogo :size="48" />
          </div>
          <div class="flex items-center gap-8">
            <span class="text-[15px] font-normal text-[#3c4947]"
              >Alla recept</span
            >
            <span class="text-[15px] font-normal text-[#3c4947]"
              >Kategorier</span
            >
            <span class="text-[15px] font-normal text-[#3c4947]"
              >Tekniker &amp; verktyg</span
            >
          </div>
          <div class="flex items-center gap-2">
            <!-- Expanded search -->
            <input
              type="search"
              placeholder="Sök recept…"
              class="w-[260px] py-1.5 px-3 text-sm rounded-lg border border-teal-400 bg-[#f8fafa] focus:outline-none text-[#191c1e]"
            />
            <button
              class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
            >
              <X :size="18" class="text-[#3c4947]" />
            </button>
            <button
              class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
            >
              <Heart :size="18" class="text-[#3c4947]" />
            </button>
            <button
              class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
            >
              <User :size="18" class="text-[#3c4947]" />
            </button>
          </div>
        </div>
      </div>
    </PreviewFull>

    <!-- Mobile -->
    <PreviewFull label="Mobil — stängd">
      <div
        class="w-full max-w-[390px] bg-white rounded-xl border border-[#eceef0] overflow-hidden"
      >
        <div class="px-4 h-16 flex items-center justify-between">
          <div class="flex items-center gap-2.5 shrink-0">
            <EmmalagatLogo :size="48" />
          </div>
          <div class="flex items-center gap-1">
            <button
              class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
            >
              <Search :size="18" class="text-[#3c4947]" />
            </button>
            <button
              class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
            >
              <Menu :size="22" class="text-[#191c1e]" />
            </button>
          </div>
        </div>
      </div>
    </PreviewFull>

    <!-- Mobile menu open -->
    <PreviewFull label="Mobil — meny öppen">
      <div
        class="w-full max-w-[390px] bg-white rounded-xl border border-[#eceef0] overflow-hidden"
      >
        <div class="px-4 h-16 flex items-center justify-between">
          <div class="flex items-center gap-2.5 shrink-0">
            <EmmalagatLogo :size="48" />
          </div>
          <div class="flex items-center gap-1">
            <button
              class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
            >
              <Search :size="18" class="text-[#3c4947]" />
            </button>
            <button
              class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
            >
              <X :size="22" class="text-[#191c1e]" />
            </button>
          </div>
        </div>
        <!-- Dropdown -->
        <div class="border-t border-[#eceef0] bg-white shadow-lg">
          <div class="flex flex-col px-6 py-2">
            <span
              class="py-4 border-b border-[#f2f4f6] text-base font-bold text-teal-500 cursor-pointer"
              >Alla recept</span
            >
            <span
              class="py-4 border-b border-[#f2f4f6] text-base font-medium text-[#191c1e] cursor-pointer"
              >Kategorier</span
            >
            <span
              class="py-4 text-base font-medium text-[#191c1e] cursor-pointer"
              >Tekniker &amp; verktyg</span
            >
          </div>
        </div>
      </div>
    </PreviewFull>

    <CodeBlock :tabs="snippetTabs" />
  </div>
</template>

<script setup>
import { Search, Heart, User, X, Menu } from "lucide-vue-next";
import EmmalagatLogo from "@/components/icons/EmmalagatLogo.vue";
import PreviewFull from "../components/PreviewFull.vue";
import CodeBlock from "../components/CodeBlock.vue";

const htmlCode = `<!-- Wrappad i Navigation.vue → AppLayout.vue -->
<nav class="bg-white border-b border-[#eceef0] sticky top-0 z-50">
  <DesktopNav />  <!-- hidden på mobil (md:flex) -->
  <MobileNav />   <!-- synlig på mobil (md:hidden) -->
</nav>

<!-- Aktiv navlänk — desktop -->
<Link href="/recept" class="relative text-[15px] font-semibold text-[#191c1e]">
  Alla recept
  <span class="absolute bottom-[-2px] left-0 right-0 h-0.5 bg-teal-500 rounded-full" />
</Link>

<!-- Inaktiv navlänk — desktop -->
<Link href="/kategorier" class="text-[15px] font-normal text-[#3c4947]">
  Kategorier
</Link>

<!-- Ikonknapp -->
<button class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors">
  <Search :size="18" class="text-[#3c4947]" />
</button>`;

const cssCode = `/* Nav-container */
background: white;
border-bottom: 1px solid #eceef0;
position: sticky;
top: 0;
z-index: 50;
height: 64px;       /* h-16 */
max-width: 1280px;
padding: 0 24px;    /* px-6 */

/* Aktiv länk */
font-size: 15px;
font-weight: 600;
color: #191c1e;
/* Underline */
position: absolute; bottom: -2px;
height: 2px; background: #14b8a6;
border-radius: 9999px;

/* Inaktiv länk */
font-size: 15px;
font-weight: 400;
color: #3c4947;

/* Ikonknapp hover */
background: #f2f4f6;
border-radius: 9999px;

/* Sökfält (expanderat) */
width: 260px;
border: 1px solid #2dd4bf;   /* border-teal-400 */
background: #f8fafa;
border-radius: 8px;
transition: max-width 300ms ease-in-out, opacity 300ms;

/* Mobil aktiv länk */
font-weight: 700;
color: #14b8a6;   /* text-teal-500 */`;

const snippetTabs = [
  { label: "HTML", code: htmlCode },
  { label: "CSS", code: cssCode, lang: "css" },
];
</script>
